'use client';

import { MarkdownPlugin } from '@platejs/markdown';
import type { Descendant } from 'platejs';
import type { PlateEditor } from 'platejs/react';
import { createPlateEditor } from 'platejs/react';

import { FieldEditorKit } from '@/components/editor/plugins/field-editor-kit';
import { humanizeRichTextMarkdown, MARKDOWN_FIELD_MAX_LENGTH, plateDocumentCharCount } from '@/lib/markdown-field';

let deserializeEditor: PlateEditor | null = null;

function getDeserializeEditor(): PlateEditor {
  if (!deserializeEditor) {
    deserializeEditor = createPlateEditor({ plugins: FieldEditorKit });
  }
  return deserializeEditor;
}

/** Deserialize markdown with the same Plate kit as field/document editors. */
export function deserializeRichTextMarkdown(markdown: string): Descendant[] {
  const normalized = humanizeRichTextMarkdown(markdown);
  return getDeserializeEditor()
    .getApi(MarkdownPlugin)
    .markdown.deserialize(normalized || '');
}

/**
 * Visible character count — matches Preview (`plateDocumentCharCount` on the Plate doc).
 * Ignores markdown syntax, structural newlines, and empty blocks.
 */
export function richTextVisibleCharCount(markdown: string): number {
  const normalized = humanizeRichTextMarkdown(markdown);
  if (!normalized.trim()) return 0;
  return plateDocumentCharCount(deserializeRichTextMarkdown(normalized));
}

/** Count from an in-memory Plate document (Preview typing). */
export function richTextVisibleCharCountFromNodes(nodes: Descendant[]): number {
  return plateDocumentCharCount(nodes);
}

/** Client-side length guard matching `yeshuman.markdown_field` visible-text semantics. */
export function markdownFieldLengthError(value: string, label: string): string | null {
  if (richTextVisibleCharCount(value) > MARKDOWN_FIELD_MAX_LENGTH) {
    return `${label} must be at most ${MARKDOWN_FIELD_MAX_LENGTH} characters.`
  }
  return null
}
